/**
 * usePlanningMessageEnhancer Hook
 *
 * Sets up the message enhancer for planning mode to inject automated reminders
 * about UI-driven changes (slides added, updated, deleted, reordered).
 *
 * This hook should be used in a component that:
 * 1. Has access to the SlideOperationsProvider context
 * 2. Is rendered when planning mode is active
 *
 * The enhancer is automatically cleared when the component unmounts.
 */
import { useEffect, useCallback, useRef } from "react";
import { useSlideOperations } from "../pages/planning/context/InsertedSlidesContext";
import { setMessageEnhancer } from "./useSendMessage";
import { enhanceMessageWithReminders } from "../utils/buildAutomatedReminder";
/**
 * Hook to set up automated reminders for planning mode
 *
 * Usage:
 * ```tsx
 * const { clearOperationsAfterSend } = usePlanningMessageEnhancer({
 *   currentPlan,
 *   includePlanContext: true,
 * });
 * ```
 */
export function usePlanningMessageEnhancer(options = {}) {
    const { currentPlan, includePlanContext = false, enabled = true } = options;
    const { slideOperations, sectionOperations, clearSlideOperations, clearSectionOperations, hasUnreportedOperations, } = useSlideOperations();
    // Use refs to avoid stale closures in the enhancer
    const slideOperationsRef = useRef(slideOperations);
    const sectionOperationsRef = useRef(sectionOperations);
    const currentPlanRef = useRef(currentPlan);
    const hasUnreportedRef = useRef(hasUnreportedOperations);
    // Keep refs updated
    useEffect(() => {
        slideOperationsRef.current = slideOperations;
    }, [slideOperations]);
    useEffect(() => {
        sectionOperationsRef.current = sectionOperations;
    }, [sectionOperations]);
    useEffect(() => {
        currentPlanRef.current = currentPlan;
    }, [currentPlan]);
    useEffect(() => {
        hasUnreportedRef.current = hasUnreportedOperations;
    }, [hasUnreportedOperations]);
    // Create the enhancer function
    const enhancer = useCallback((message) => {
        // Only enhance if there are unreported operations
        if (!hasUnreportedRef.current) {
            // Still include plan context if requested
            if (includePlanContext && currentPlanRef.current) {
                return enhanceMessageWithReminders(message, {
                    slideOperations: [],
                    sectionOperations: [],
                    currentPlan: currentPlanRef.current,
                    includePlanContext: true,
                });
            }
            return message;
        }
        console.log('[usePlanningMessageEnhancer] Enhancing message with operations:', {
            slideOps: slideOperationsRef.current.length,
            sectionOps: sectionOperationsRef.current.length,
        });
        const enhanced = enhanceMessageWithReminders(message, {
            slideOperations: slideOperationsRef.current,
            sectionOperations: sectionOperationsRef.current,
            currentPlan: includePlanContext ? currentPlanRef.current : undefined,
            includePlanContext,
        });
        // Clear operations after including them in the reminder
        // This prevents sending the same operations multiple times
        clearSlideOperations();
        clearSectionOperations();
        return enhanced;
    }, [includePlanContext, clearSlideOperations, clearSectionOperations]);
    // Set up the enhancer when enabled, clear when disabled or unmounted
    useEffect(() => {
        if (enabled) {
            setMessageEnhancer(enhancer);
            console.log('[usePlanningMessageEnhancer] Enhancer enabled');
        }
        else {
            setMessageEnhancer(undefined);
            console.log('[usePlanningMessageEnhancer] Enhancer disabled');
        }
        // Cleanup on unmount
        return () => {
            setMessageEnhancer(undefined);
            console.log('[usePlanningMessageEnhancer] Enhancer cleared (unmount)');
        };
    }, [enabled, enhancer]);
    // Manual function to clear operations (if needed externally)
    const clearOperationsAfterSend = useCallback(() => {
        clearSlideOperations();
        clearSectionOperations();
    }, [clearSlideOperations, clearSectionOperations]);
    return {
        /** Clear operations manually (usually not needed as enhancer clears automatically) */
        clearOperationsAfterSend,
        /** Whether there are operations that will be included in the next message */
        hasUnreportedOperations,
        /** Number of pending slide operations */
        pendingSlideOperations: slideOperations.length,
        /** Number of pending section operations */
        pendingSectionOperations: sectionOperations.length,
    };
}
export default usePlanningMessageEnhancer;
